import { AspectRatio, Image, Loader, Popover, ScrollArea } from '@mantine/core'
import { AnimatedBackground } from '../common/components/background/AnimatedBackground'
import classes from './Navigation.module.css'
import AvatarMe from '../auth/AvatarMe'
import {
  IconBell,
  IconChevronRight,
  IconNews,
  IconX,
} from '@tabler/icons-react'
import { Typography } from '../common/typography'
import { useIsPlayer } from '../common/api'
import { Link, useLocation, useNavigate } from 'react-router'
import {
  useNotificationsInfinite,
  Notification,
  useNotificationsCount,
  useMarkAllAsRead,
  useMarkAsRead,
} from '../notifications/api'
import { useDisclosure } from '@mantine/hooks'

export default function NavigationHeader({
  user,
  isMobile,
  setMobileNavigationOpen,
}: {
  user: string
  isMobile: boolean
  setMobileNavigationOpen: React.Dispatch<React.SetStateAction<boolean>>
}) {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const {
    data: notificationsInfinite,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useNotificationsInfinite()
  const [opened, { toggle, close }] = useDisclosure(false)
  const { data: notificationsCount } = useNotificationsCount()
  const { mutate: markAllAsRead } = useMarkAllAsRead()
  const { mutate: markAsRead } = useMarkAsRead()

  const notifications =
    notificationsInfinite?.pages
      .flatMap((page) => page.data)
      .filter((notification) => {
        if (notification.entity_type === 'news') {
          return notification.metadata.news_id !== undefined
        }
        return true
      }) ?? []

  function handleNotificationSelect(notification: Notification) {
    markAsRead({ notification_id: notification.notification_id })
    close()
    if (notification.entity_type === 'news') {
      navigate(`/news/${notification.metadata.news_id}`)
    } else if (notification.entity_type === 'post') {
      navigate(
        `/competitions/${notification.metadata.competition_id}/arena#post-${notification.metadata.post_id}`
      )
    } else if (notification.entity_type === 'comment') {
      navigate(
        `/competitions/${notification.metadata.competition_id}/arena#post-${notification.metadata.post_id}-comment-${notification.metadata.comment_id}`
      )
    }
  }

  return (
    <div className={classes.navigationHeader} data-mobile={isMobile}>
      <AnimatedBackground radial />
      <div className={classes.navigationHeaderContent}>
        <AvatarMe size="lg" />
        <div className={classes.navigationHeaderRow}>
          <div className={classes.navigationHeaderItem}>
            <Typography fc={{ light: 'white', dark: 'white' }}>
              {user}
            </Typography>
            <MyProfileLink
              onNavigate={
                isMobile ? () => setMobileNavigationOpen(false) : undefined
              }
            />
          </div>
          {isMobile ? (
            <IconX
              color="white"
              onClick={() => setMobileNavigationOpen(false)}
            />
          ) : pathname.includes('home') ? null : (
            <Popover
              opened={opened}
              withArrow
              arrowSize={12}
              position="right-start"
              onDismiss={close}
            >
              <Popover.Target>
                <div className={classes.notificationsContainer}>
                  <IconBell className={classes.headerIcon} onClick={toggle} />
                  {notificationsCount.count > 0 && (
                    <div
                      className={classes.notificationsCount}
                      onClick={toggle}
                    >
                      {notificationsCount.count}
                    </div>
                  )}
                </div>
              </Popover.Target>
              <Popover.Dropdown className={classes.popoverDropdown}>
                <div className={classes.popoverHeader}>
                  <div className={classes.popoverHeaderWrapper}>
                    <Typography fs="lg" fw={500} lh="125">
                      Notifications
                    </Typography>
                    <div
                      style={{ cursor: 'pointer' }}
                      onClick={() => markAllAsRead()}
                    >
                      <Typography
                        fs="xs"
                        lh="150"
                        fc={{ light: 'primary-5', dark: 'primary-4' }}
                        underline
                      >
                        Mark all as read
                      </Typography>
                    </div>
                  </div>
                  <IconX style={{ cursor: 'pointer' }} onClick={close} />
                </div>
                <ScrollArea mah={750}>
                  {notifications.length > 0 ? (
                    notifications.map((notification) => (
                      <div
                        key={notification.notification_id}
                        className={classes.notificationRow}
                        data-read={!notification.is_read}
                        onClick={() => handleNotificationSelect(notification)}
                      >
                        {notification.entity_type === 'post' ||
                        notification.entity_type === 'comment' ? (
                          <AspectRatio ratio={1 / 1}>
                            <Image
                              src={notification.metadata.author_photo.thumbnail}
                              alt={notification.metadata.author_name}
                              className={classes.authorImage}
                              fit="scale-down"
                            />
                          </AspectRatio>
                        ) : (
                          <div className={classes.newsContainer}>
                            <IconNews className={classes.newsIcon} />
                          </div>
                        )}
                        <div className={classes.notificationContainer}>
                          <Typography lh="140">
                            {notification.content}
                          </Typography>
                        </div>
                        <IconChevronRight
                          className={classes.notificationIcon}
                          data-read={!notification.is_read}
                        />
                      </div>
                    ))
                  ) : (
                    <Typography
                      ta="center"
                      className={classes.emptyNotifications}
                    >
                      No new notifications.
                    </Typography>
                  )}
                  {hasNextPage && (
                    <div
                      className={classes.loaderContainer}
                      onClick={() => fetchNextPage()}
                    >
                      {isFetchingNextPage ? (
                        <Loader color="#0B6B7B" />
                      ) : (
                        <Typography
                          fs="xs"
                          lh="150"
                          fc={{ light: 'primary-5', dark: 'primary-4' }}
                          underline
                        >
                          Load More
                        </Typography>
                      )}
                    </div>
                  )}
                </ScrollArea>
              </Popover.Dropdown>
            </Popover>
          )}
        </div>
      </div>
    </div>
  )
}

function MyProfileLink({ onNavigate }: { onNavigate?: () => void }) {
  const { player_id, competition_id } = useIsPlayer()
  const to = player_id
    ? `/competitions/${competition_id}/players/${player_id}`
    : `/settings`
  return (
    <Link className={classes.headerLink} to={to} onClick={onNavigate}>
      {player_id ? 'My profile' : 'Manager'}
      <IconChevronRight size={18} stroke={2.5} color="white" />
    </Link>
  )
}
